import { Spinner, TextArea } from "@blueprintjs/core"
import { observer } from "mobx-react"
import { PlanrButton } from "modules/common/components/planr/button/Button"
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon"
import React, { useCallback, useRef, useState } from "react"
import { Droppable } from "@hello-pangea/dnd"
import { BoardColumnType, SystemColumns } from "../models/board"
import { ColumnPageType, PAGE_SIZE, QuestionCardType } from "../models/card"
import styles from "./BoardColumnView.module.scss"
import { QuestionCard } from "./QuestionCard"

/** За сколько пикселей до низа колонки просим следующую страницу */
const LOAD_AHEAD = 160

export const BoardColumnView: React.FC<BoardColumnProps> = observer(
  ({
    column,
    page,
    hideProject,
    currentUserId,
    canAdd,
    dropDisabled,
    tagColors,
    revealedId,
    compact,
    onOpen,
    onMenu,
    onQuickAdd,
    onLoadMore,
  }) => {
    const [adding, setAdding] = useState(false)
    const [title, setTitle] = useState("")
    const [saving, setSaving] = useState(false)
    /** Заголовок карточки, которая сейчас сохраняется: показываем её призраком в колонке */
    const [pending, setPending] = useState("")
    const input = useRef<HTMLTextAreaElement | null>(null)

    const expired = column.key === SystemColumns.expired
    const questions: QuestionCardType[] = page ? page.items.slice() : []
    const total = page ? page.total : 0
    const hasMore = !!page && page.hasMore
    const loading = !!page && page.loading

    const startAdding = useCallback(() => {
      setAdding(true)
      setTimeout(() => input.current?.focus(), 0)
    }, [])

    const cancel = useCallback(() => {
      setAdding(false)
      setTitle("")
    }, [])

    const submit = useCallback(async () => {
      const value = title.trim()

      if (saving) {
        return
      }

      if (!value) {
        cancel()
        return
      }

      // поле освобождаем сразу, а карточку показываем призраком, пока она сохраняется
      setTitle("")
      setPending(value)
      setSaving(true)

      await onQuickAdd(column.key, value)

      setSaving(false)
      setPending("")

      // задание заведено — закрываем форму, а не оставляем её ждать следующего
      cancel()
    }, [title, saving, column.key, onQuickAdd, cancel])

    const onKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (e.key === "Enter" && !e.shiftKey) {
          e.preventDefault()
          submit()
        }

        if (e.key === "Escape") {
          cancel()
        }
      },
      [submit, cancel]
    )

    // архивную колонку прокруткой не догружаем: в ней задания копятся годами
    const onScroll = useCallback(
      (e: React.UIEvent<HTMLDivElement>) => {
        if (column.isArchive || !hasMore || loading) {
          return
        }

        const list = e.currentTarget

        if (list.scrollHeight - list.scrollTop - list.clientHeight < LOAD_AHEAD) {
          onLoadMore(column.key)
        }
      },
      [column.isArchive, column.key, hasMore, loading, onLoadMore]
    )

    return (
      <div className={`${styles.column} ${compact ? styles.compact : ""}`}>
        <div className={styles.header} style={{ borderColor: column.color }}>
          <div className={styles.name} title={column.name}>
            <span className={styles.dot} style={{ backgroundColor: column.color }} />
            {column.name}
            {column.isArchive && (
              <span className={styles.archive} title="Задания в колонке копятся: показываются частями">
                архив
              </span>
            )}
          </div>
          <div className={styles.count} style={{ color: column.color, borderColor: column.color }}>
            {total}
          </div>
        </div>

        <Droppable droppableId={column.key} isDropDisabled={dropDisabled}>
          {(provided, snapshot) => (
            <div
              ref={provided.innerRef}
              {...provided.droppableProps}
              onScroll={onScroll}
              className={`${styles.list} ${snapshot.isDraggingOver ? styles.over : ""} ${
                dropDisabled ? styles.locked : ""
              }`}
            >
              {questions.map((question, index) => (
                <QuestionCard
                  key={question.id}
                  question={question}
                  index={index}
                  color={column.color}
                  archive={column.isArchive}
                  tagColors={tagColors}
                  hideProject={hideProject}
                  currentUserId={currentUserId}
                  revealed={question.id === revealedId}
                  compact={compact}
                  onOpen={onOpen}
                  onMenu={onMenu}
                />
              ))}
              {provided.placeholder}

              {questions.length === 0 && !snapshot.isDraggingOver && (
                <div className={styles.empty}>{expired ? "Просроченных заданий нет" : "Пусто"}</div>
              )}

              {hasMore && column.isArchive && (
                <button type="button" className={styles.more} onClick={() => onLoadMore(column.key)} disabled={loading}>
                  {loading ? (
                    <Spinner size={14} intent="primary" />
                  ) : (
                    <>
                      Показать ещё {Math.min(PAGE_SIZE, total - questions.length)}
                      <span className={styles.moreRest}>из {total - questions.length}</span>
                    </>
                  )}
                </button>
              )}

              {loading && !column.isArchive && (
                <div className={styles.loading}>
                  <Spinner size={16} intent="primary" />
                </div>
              )}
            </div>
          )}
        </Droppable>

        {canAdd && column.canQuickAdd && (
          <div className={styles.adder}>
            {saving && (
              <div className={styles.ghost}>
                <div className={styles.ghostTitle}>{pending}</div>
                <Spinner size={14} intent="primary" className={styles.ghostSpinner} />
              </div>
            )}

            {adding ? (
              <div className={styles.form}>
                <TextArea
                  inputRef={(el: HTMLTextAreaElement | null) => {
                    input.current = el
                  }}
                  value={title}
                  placeholder="Заголовок задания"
                  growVertically={true}
                  className={styles.input}
                  onChange={(e) => setTitle(e.target.value)}
                  onKeyDown={onKeyDown}
                />
                <div className={styles.formButtons}>
                  <PlanrButton type="greenish" size="small" onClick={submit} disabled={saving || !title.trim()}>
                    Добавить
                  </PlanrButton>
                  <PlanrButton type="graybtn" size="small" onClick={cancel}>
                    Отмена
                  </PlanrButton>
                </div>
              </div>
            ) : (
              <button type="button" className={styles.addButton} onClick={startAdding}>
                <GeneralIcon type="general-plus-small" size={12} />
                Добавить задание
              </button>
            )}
          </div>
        )}
      </div>
    )
  }
)

interface BoardColumnProps {
  column: BoardColumnType
  /** Загруженная страница колонки */
  page?: ColumnPageType
  hideProject?: boolean
  currentUserId: string
  canAdd: boolean
  dropDisabled: boolean
  /** Цвета меток из справочника */
  tagColors: TStringMap<string>
  /** Карточка, к которой колонка прокручивается и подсвечивает её */
  revealedId?: string
  /** Узкая доска виджета главной: колонка уже, карточки плотнее */
  compact?: boolean
  onOpen: (question: { id: string }) => void
  /** Меню действий по правой кнопке на карточке */
  onMenu?: (question: QuestionCardType, x: number, y: number) => void
  onQuickAdd: (columnId: string, title: string) => Promise<any>
  onLoadMore: (columnKey: string) => void
}
